import type { Place, PlaceKind } from './types';

// Mint-TMS-Places · logic bản đồ điểm (camera, ghim, hãng tàu, thẻ chi tiết). Chuyển từ script board (loc-hub.js), không có DOM.

export const KIND_COLOR: Record<PlaceKind, string> = { Cảng: 'var(--d-blue)', ICD: 'var(--d-green)', Depot: 'var(--d-violet)', Kho: 'var(--d-blue)', Tuyến: 'var(--d-violet)', Trạm: 'var(--d-amber)' };
export const KIND_ORDER: readonly PlaceKind[] = ['Cảng', 'ICD', 'Depot'];
/** Tỉnh của Kho theo thứ tự nhóm trong danh sách + màu (board WHG). */
export const WH_PROVINCES: ReadonlyArray<readonly [string, string]> = [['TP.HCM', 'var(--d-blue)'], ['Đồng Nai', 'var(--d-green)'], ['Tây Ninh', 'var(--d-green)'], ['Vĩnh Long', 'var(--d-violet)'], ['Đồng Tháp', 'var(--d-violet)'], ['Cần Thơ', 'var(--d-violet)']];
export type WhSub = 'all' | 'hcm' | 'dnb' | 'tnb' | 'lim';
const WEST = ['Vĩnh Long', 'Đồng Tháp', 'Cần Thơ'];
const WH_SUB: Record<WhSub, (p: Place) => boolean> = { all: () => true, hcm: (p) => p.province === 'TP.HCM', dnb: (p) => p.province === 'Đồng Nai' || p.province === 'Tây Ninh', tnb: (p) => WEST.includes(p.province ?? ''), lim: (p) => !!whRestrict(p) };
export const WH_SUBS: ReadonlyArray<readonly [WhSub, string]> = [['all', 'Tất cả'], ['hcm', 'TP.HCM'], ['dnb', 'Đông Nam Bộ'], ['tnb', 'Miền Tây'], ['lim', 'Hạn chế xe']];
export const whRestrict = (p: Place) => { const r = p.restrict; return r && (r.ban.length || r.ton || r.veh.length || r.road) ? r : null; };
export const whRestrictText = (p: Place) => { const r = whRestrict(p); return r ? [r.ban.length ? `Cấm tải ${r.ban.join(', ')}` : '', r.ton ? `Tối đa ${r.ton} tấn` : '', r.veh.length ? `Chỉ nhận ${r.veh.join(', ')}` : '', r.road].filter(Boolean).join(' · ') : ''; };
export const WH_VEHICLES = ['20′', '40′', '45′', 'Xe tải'] as const;
export const whStatus = (p: Place): { key: 'ok' | 'warn' | 'err'; label: string } => (!hasXY(p) ? { key: 'err', label: 'Thiếu toạ độ' } : p.approved ? { key: 'ok', label: 'Đã xác thực' } : { key: 'warn', label: 'Chưa xác thực' });
export function filterWarehouses(list: readonly Place[], sub: WhSub, q: string, pendingOnly: boolean): Place[] {
  const ql = q.trim().toLowerCase();
  return list.filter((p) => WH_SUB[sub](p) && (!pendingOnly || !p.approved) && (!ql || `${p.code} ${p.name} ${p.area ?? ''} ${p.address ?? ''} ${p.customers ?? ''}`.toLowerCase().includes(ql)));
}
export const PENDING_ALL = 18;
export const TOTALS = { dep: 128, wh: 340, rt: 383, toll: 207 } as const;

export const hasXY = (p: Place): p is Place & { lat: number; lng: number } => p.lat != null && p.lng != null;
export const isPending = (p: Place) => !p.approved;
export const statusOf = (p: Place): { key: 'ok' | 'warn' | 'err'; label: string } =>
  !hasXY(p) ? { key: 'err', label: 'Thiếu toạ độ' } : p.approved ? { key: 'ok', label: 'Đã duyệt' } : { key: 'warn', label: 'Chờ duyệt' };

export interface Carrier { code: string; type: '' | 'DC' | 'RF' }
/** "CKL EMC/RF POS/DC" → [{CKL,''},{EMC,RF}…]; hãng có cả DC lẫn RF → type ''. */
export function carriersOf(s: string | undefined): Carrier[] {
  const mp = new Map<string, Set<string>>();
  for (const tok of (s ?? '').split(' ').filter(Boolean)) {
    const [c = '', ty] = tok.split('/');
    const set = mp.get(c) ?? new Set<string>();
    if (ty) set.add(ty);
    mp.set(c, set);
  }
  return [...mp].map(([code, ty]) => ({ code, type: ty.size === 1 ? ([...ty][0] as 'DC' | 'RF') : '' }));
}

export const fmtXY = (p: Place) => (hasXY(p) ? `${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}` : 'Chưa có · bấm Sửa để định vị');
export const fmtRadius = (m: number | null | undefined) => (m ? (m >= 1000 ? `${(m / 1000).toFixed(1).replace('.', ',')} km` : `${m} m`) : '—');
export const provinceOf = (p: Place) => (p.lat != null && p.lat > 15 ? 'Hải Phòng' : 'TP.HCM');

/** Tuyến xuất phát hiển thị trong thẻ điểm: board dựng từ n tuyến đầu danh sách mẫu (kho + km) khi điểm chưa có tuyến gắn mã. */
export const ROUTE_SAMPLE: ReadonlyArray<{ wh: string; km: number }> = [
  { wh: 'Kho Ngọc Kim Loan', km: 338.7 },
  { wh: 'Kho Thái Sơn', km: 351 },
  { wh: 'Kho Hoàng Lai (Sài Gòn)', km: 178.8 },
  { wh: 'Kho N&P, Bến Tre', km: 216.7 },
];
export const routeRows = (p: Place) => ROUTE_SAMPLE.slice(0, Math.min(p.routes ?? 0, 4));

// ---- camera: bản đồ "bay" tới điểm được chọn (hằng số theo board) ----
const S = 600;
const KK = 0.982;
const LNG0 = 105.2;
const LAT0 = 11.4;
export const wx = (lng: number) => (lng - LNG0) * KK * S;
export const wy = (lat: number) => (LAT0 - lat) * S;
export const WORLD = { w: Math.round(wx(107.7)), h: Math.round(wy(9.3)) };

export interface Cam { z: number; tx: number; ty: number }
/** Khung bản đồ `mw × mh`; cột trái `leftW` (420 khi có chi tiết, 360 khi là danh sách). Điểm chọn → zoom cố định 9, không chọn → ôm hết điểm phía Nam. */
export function camera(places: readonly Place[], sel: Place | null, mw: number, mh: number, leftW: number, zoomSteps: number): Cam {
  const box = { l: 12 + leftW + 28, r: mw - 64, t: 12 + 40 + 10 + 20, b: mh - 56 };
  const pts: Array<[number, number]> = sel && hasXY(sel) ? [[sel.lat, sel.lng]] : places.filter((p) => hasXY(p) && p.lat <= 15).map((p) => [p.lat as number, p.lng as number]);
  if (!pts.length) pts.push([10.75, 106.45], [10.2, 105.8], [11.0, 106.9]);
  const xs = pts.map((p) => wx(p[1]));
  const ys = pts.map((p) => wy(p[0]));
  const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
  const bw = Math.max(x1 - x0, 8), bh = Math.max(y1 - y0, 8);
  const base = sel && hasXY(sel) ? 9 : Math.min((box.r - box.l) / (bw * 1.15), (box.b - box.t) / (bh * 1.15), 9);
  const z = Math.max(0.3, Math.min(16, base * Math.pow(1.6, zoomSteps)));
  return { z, tx: (box.l + box.r) / 2 - ((x0 + x1) / 2) * z, ty: (box.t + box.b) / 2 - ((y0 + y1) / 2) * z };
}
export const project = (c: Cam, lat: number, lng: number) => ({ x: wx(lng) * c.z + c.tx, y: wy(lat) * c.z + c.ty });
/** bán kính geofence (m) → đường kính px trên màn */
export const ringDiameter = (c: Cam, radius: number) => Math.max(10, ((radius / 111000) * S * c.z) * 2);

const pth = (pts: ReadonlyArray<readonly [number, number]>) => pts.map(([a, o], i) => `${i ? 'L' : 'M'}${wx(o).toFixed(1)} ${wy(a).toFixed(1)}`).join(' ');
/** Nền bản đồ gần đúng: bờ biển, sông, cao tốc (vẽ theo toạ độ nên bay cùng camera). */
export const GEO = {
  sea: `${pth([[9.3, 106.45], [9.55, 106.5], [9.85, 106.6], [10.1, 106.7], [10.28, 106.85], [10.36, 107.02], [10.34, 107.09], [10.42, 107.24], [10.55, 107.45], [10.72, 107.7], [9.3, 107.7]])} Z`,
  rivers: [
    [[10.98, 106.62], [10.88, 106.69], [10.8, 106.71], [10.77, 106.75], [10.72, 106.76], [10.64, 106.79], [10.55, 106.86], [10.45, 106.96]],
    [[10.95, 106.86], [10.86, 106.8], [10.78, 106.76]],
    [[10.85, 105.2], [10.6, 105.48], [10.42, 105.72], [10.3, 105.98], [10.33, 106.24], [10.3, 106.45], [10.22, 106.72]],
    [[10.75, 105.1], [10.42, 105.4], [10.05, 105.76], [9.85, 105.96], [9.6, 106.25], [9.48, 106.42]],
  ].map((p, i) => ({ d: pth(p as Array<[number, number]>), w: i < 2 ? 5 : 7 })),
  roads: [
    [[10.92, 106.86], [10.86, 106.73], [10.76, 106.6], [10.65, 106.45], [10.5, 106.3], [10.41, 106.15], [10.3, 105.96], [10.24, 105.86], [10.05, 105.75], [9.8, 105.6]],
    [[10.73, 106.65], [10.6, 106.48], [10.45, 106.36], [10.39, 106.3], [10.3, 106.05], [10.27, 105.91]],
    [[10.79, 106.76], [10.8, 106.87], [10.86, 107.0], [10.95, 107.16]],
    [[10.82, 106.71], [10.92, 106.71], [11.02, 106.68], [11.18, 106.65]],
    [[10.94, 106.88], [10.76, 107.0], [10.6, 107.05], [10.42, 107.1]],
    [[10.8, 106.6], [10.9, 106.5], [11.02, 106.34]],
    [[10.36, 106.36], [10.24, 106.38], [10.1, 106.4]],
  ].map((p, i) => ({ d: pth(p as Array<[number, number]>), w: i < 3 ? 4 : 2.5 })),
};
export const AREAS: ReadonlyArray<readonly [string, number, number]> = [['TP.HCM', 10.79, 106.66], ['Thủ Đức', 10.86, 106.79], ['Biên Hoà', 10.96, 106.84], ['Cái Mép', 10.55, 107.06], ['Bến Lức', 10.64, 106.48], ['Mỹ Tho', 10.36, 106.33], ['Bến Tre', 10.21, 106.42], ['Vĩnh Long', 10.24, 105.98], ['Cần Thơ', 10.02, 105.76], ['Cao Lãnh', 10.46, 105.63]];

export type SubKey = 'all' | PlaceKind;
export function filterPlaces(list: readonly Place[], sub: SubKey, q: string, pendingOnly: boolean): Place[] {
  const ql = q.trim().toLowerCase();
  return list.filter((p) => (sub === 'all' || p.kind === sub) && (!pendingOnly || isPending(p)) && (!ql || `${p.code} ${p.name} ${p.area ?? ''} ${p.carriers ?? ''}`.toLowerCase().includes(ql)));
}
/** Số điểm phía Bắc (Hải Phòng) / Nam ngoài khung hiện tại, cho nút "+N điểm phía …". */
export function outOfFrame(list: readonly Place[], sel: Place | null) {
  const north = !!sel && sel.lat != null && sel.lat > 15;
  const n = list.filter((p) => hasXY(p) && (north ? p.lat <= 15 : p.lat > 15)).length;
  return { n, side: north ? 'Nam' : 'Bắc' } as const;
}
